import React from 'react';
import { Card, Badge } from 'react-bootstrap';

// Props do componente Stepper
interface StepperProps {
    steps: Array<{
        id: string;
        label: string;
    }>;
    current: string;
    onSelect: (id: string) => void;
    completed: Set<string>;
    className?: string;
}

// Componente do Stepper Moderno
const ModernStepper: React.FC<StepperProps> = ({ 
    steps, 
    current, 
    onSelect, 
    completed,
    className = '' 
}) => {
    return (
        <Card className={`shadow-sm mb-4 ${className}`}>
            <Card.Body>
                {/* Cabeçalho com título e indicador de progresso */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Definir Membros da Banca</h5>
                    <div className="d-flex align-items-center">
                        <span className="me-2 small text-muted">
                            {steps.findIndex(step => step.id === current) + 1}/{steps.length}
                        </span>
                    </div>
                </div>

                {/* Stepper horizontal */}
                <div className="d-flex justify-content-between align-items-center position-relative">
                    {/* Linha de fundo */}
                    <div 
                        className="position-absolute top-50 start-0 end-0 bg-light" 
                        style={{ 
                            height: '2px', 
                            zIndex: 1,
                            margin: '0 20px'
                        }}
                    />
                    
                    {steps.map((step, index) => {
                        const isActive = step.id === current;
                        const isCompleted = completed.has(step.id) && !isActive;
                        const isLast = index === steps.length - 1;
                        const stepNumber = index + 1;
                        
                        return (
                            <div 
                                key={step.id} 
                                className="d-flex flex-column align-items-center position-relative" 
                                style={{ zIndex: 2, flex: 1 }}
                            >
                                {/* Container do passo */}
                                <div className="d-flex flex-column align-items-center">
                                    {/* Círculo do passo */}
                                    <div
                                        className={`d-flex align-items-center justify-content-center rounded-circle border ${
                                            isActive 
                                                ? 'bg-primary border-primary text-white shadow' 
                                                : isCompleted 
                                                    ? 'bg-success border-success text-white'
                                                    : 'bg-white border-secondary text-muted'
                                        }`}
                                        style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontSize: '0.9rem',
                                            fontWeight: '600'
                                        }}
                                        onClick={() => onSelect(step.id)}
                                        title={`Ir para: ${step.label}`}
                                    >
                                        {isCompleted ? (
                                            <i className="bi bi-check-lg" style={{ fontSize: '1.1rem' }} />
                                        ) : (
                                            <span>{stepNumber}</span>
                                        )}
                                    </div>
                                    
                                    {/* Label do passo */}
                                    <span 
                                        className={`mt-2 small text-center ${
                                            isActive ? 'text-primary fw-bold' : 'text-muted'
                                        }`}
                                        style={{ 
                                            maxWidth: '100px', 
                                            lineHeight: '1.2',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        {step.label}
                                    </span>
                                </div>
                                
                                {/* Linha de conexão entre passos (exceto o último) */}
                                {!isLast && (
                                    <div 
                                        className={`position-absolute top-5 ${
                                            isCompleted ? 'bg-success' : 'bg-light'
                                        }`} 
                                        style={{ 
                                            width: `calc(100% - 40px)`,
                                            height: '2px', 
                                            left: 'calc(50% + 20px)',
                                            zIndex: 1
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Indicador de passo atual para mobile */}
                <div className="d-block d-md-none text-center mt-3">
                    <Badge bg="primary" className="px-3 py-2">
                        Passo {steps.findIndex(step => step.id === current) + 1} de {steps.length}: {steps.find(step => step.id === current)?.label}
                    </Badge>
                </div>
            </Card.Body>
        </Card>
    );
};

// Versão alternativa mais compacta (opcional)
export const CompactStepper: React.FC<StepperProps> = ({ 
    steps, 
    current, 
    onSelect, 
    completed,
    className = '' 
}) => {
    return (
        <Card className={`shadow-sm mb-3 ${className}`}>
            <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <span className="me-3 small fw-bold">Progresso:</span>
                        <div className="d-none d-md-flex align-items-center">
                            {steps.map((step, index) => {
                                const isActive = step.id === current;
                                const isCompleted = completed.has(step.id);
                                
                                return (
                                    <div key={step.id} className="d-flex align-items-center">
                                        <div
                                            className={`d-flex align-items-center justify-content-center rounded-circle ${
                                                isActive 
                                                    ? 'bg-primary text-white' 
                                                    : isCompleted 
                                                        ? 'bg-success text-white'
                                                        : 'bg-light text-muted'
                                            }`}
                                            style={{ 
                                                width: '30px', 
                                                height: '30px', 
                                                cursor: 'pointer',
                                                fontSize: '0.8rem',
                                                fontWeight: '600'
                                            }}
                                            onClick={() => onSelect(step.id)}
                                        >
                                            {isCompleted ? '✓' : index + 1}
                                        </div>
                                        
                                        {index < steps.length - 1 && (
                                            <div 
                                                className={`mx-2 ${isCompleted ? 'text-success' : 'text-light'}`}
                                                style={{ fontSize: '1.5rem' }}
                                            >
                                                •
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    
                    <div className="d-flex align-items-center">
                        <span className="me-2 small text-muted">Etapa:</span>
                        <Badge bg="primary">
                            {steps.findIndex(step => step.id === current) + 1}/{steps.length}
                        </Badge>
                    </div>
                </div>
                
                {/* Label atual para mobile */}
                <div className="d-block d-md-none text-center mt-2">
                    <small className="text-muted">
                        {steps.findIndex(step => step.id === current) + 1}/{steps.length}: {steps.find(step => step.id === current)?.label}
                    </small>
                </div>
            </Card.Body>
        </Card>
    );
};

// Componente de navegação para usar com o stepper
interface StepperNavigationProps {
    currentStep: string;
    steps: Array<{ id: string; label: string }>;
    onPrevious: () => void;
    onNext: () => void;
    onSubmit?: () => void;
    isSubmitting?: boolean;
    isValid?: boolean;
    showSubmit?: boolean;
}

export const StepperNavigation: React.FC<StepperNavigationProps> = ({
    currentStep,
    steps,
    onPrevious,
    onNext,
    onSubmit,
    isSubmitting = false,
    isValid = true,
    showSubmit = false
}) => {
    const currentIndex = steps.findIndex(step => step.id === currentStep);
    const isFirstStep = currentIndex === 0;
    const isLastStep = currentIndex === steps.length - 1;

    return (
        <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
            <div>
                {!isFirstStep && (
                    <button
                        className="btn btn-outline-secondary"
                        onClick={onPrevious}
                        disabled={isSubmitting}
                    >
                        ← Anterior
                    </button>
                )}
            </div>
            
            <div className="d-flex align-items-center">
                <small className="text-muted me-3">
                    Etapa {currentIndex + 1} de {steps.length}
                </small>
                
                {!isLastStep ? (
                    <button
                        className="btn btn-primary"
                        onClick={onNext}
                        disabled={isSubmitting}
                    >
                        Próximo →
                    </button>
                ) : (
                    showSubmit && (
                        <button
                            className="btn btn-success"
                            onClick={onSubmit}
                            disabled={isSubmitting || !isValid}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Salvando...
                                </>
                            ) : (
                                'Salvar Definição'
                            )}
                        </button>
                    )
                )}
            </div>
        </div>
    );
};

export default ModernStepper;